<template>
  <div v-if="post" data-page-name="case-studies">
    <section class="hero">
      <div class="wrapper">
        <AppBreadcrumbs :items="[post.data.title]" append accent="dark" />

        <h1 v-html="post.data.title" />

        <Buttons.Link
          v-if="post.data?.data?.pdf_url"
          :to="post.data.data.pdf_url"
          fill="outline"
          accent="white"
          target="_blank"
        >
          {{ $t('Download_PDF') }}

          <SvgIcon name="download" />
        </Buttons.Link>
      </div>
    </section>

    <div class="about">
      <div class="wrapper">
        <ul class="columns">
          <li class="left">
            <img
              v-if="post.data?.data?.company_logo_url"
              :src="post.data.data.company_logo_url"
              :alt="post.data?.data?.company_logo_alt ?? ''"
            />
          </li>

          <li class="center">
            <ul class="rows">
              <li v-if="post.data?.data?.location" class="row">
                <span>{{ $t('Location') }}</span>
                <p>{{ post.data.data.location }}</p>
              </li>

              <li v-if="post.data?.data?.industry" class="row">
                <span>{{ $t('Industry') }}</span>
                <p>{{ post.data.data.industry }}</p>
              </li>
            </ul>
          </li>

          <li class="right">
            <ul class="rows">
              <li v-if="post.data?.data?.company_description" class="row">
                <span>{{ $t('Company') }}</span>
                <p>{{ post.data.data.company_description }}</p>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>

    <section class="content">
      <div class="wrapper">
        <div v-html="post?.data?.content" class="content" />
      </div>
    </section>

    <div class="feedback">
      <div class="wrapper">
        <FeedbackCard active v-bind="feedback" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createError } from '#imports';

// components
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';
import Buttons from '~/components/redesign/Buttons';
import FeedbackCard from '~/components/redesign/FeedbackCard.vue';

// utils
import get from 'lodash.get';

// constants
import { STATUS_CODES } from '~/constants';

// types
import { PostStatus } from '~/types';

const {
  params: { slug },
  query: { preview },
} = useRoute();

const { data: post } = await useAsyncGql({
  operation: 'CaseStudiesArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: data => data.blog_article,
  },
});

if (!post.value)
  throw createError({
    statusCode: STATUS_CODES.NOT_FOUND,
  });

const feedback = {
  quote: get(post.value, 'data.data.feedback_quote', ''),
  author: {
    name: get(post.value, 'data.data.author_name', ''),
    position: get(post.value, 'data.data.author_position', ''),
  },
  image: {
    src: get(post.value, 'data.data.company_logo_url', ''),
    alt: get(post.value, 'data.data.company_logo_alt', ''),
  },
};

useSeoMeta({
  title: get(post.value, 'data.seo.title'),
  ogTitle: get(post.value, 'data.seo.title'),
  description: get(post.value, 'data.seo.description'),
  ogDescription: get(post.value, 'data.seo.description'),
  ogImage: get(post.value, 'data.seo.image'),
  ogType: get(post.value, 'data.seo.og_type'),
});
</script>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/fn';
@use '_/variables/shadow';

[data-page-name='case-studies'] {
  section.hero {
    background: url('/redesign/images/purple-sphere-over-pink-bg.webp');
    background-size: max(100%, 94.5rem);
    background-repeat: no-repeat;
    background-position: 50% 0;

    .wrapper {
      padding: 2rem 2rem 11.75rem;

      @include size.wrapper(80rem);

      @include media.tablet {
        padding: 2rem 2rem 11.75rem;
      }

      @include media.mobile {
        padding: 1.5rem 1rem 11.75rem;
      }

      [data-component-name='AppBreadcrumbs'] {
        margin-bottom: 5rem;

        @include media.tablet {
          margin-bottom: 3rem;
        }

        @include media.mobile {
          margin-bottom: 1.2rem;
        }
      }

      h1 {
        color: fn.token('text-reversed');

        @include typo.h3;

        text-align: center;
        margin-bottom: 2rem;
        line-height: 3.25rem;

        @include media.tablet {
          margin-bottom: 1.5rem;
        }

        @include media.mobile {
          margin-bottom: 1.7rem;
        }
      }

      [data-component-name='Buttons.Link'] {
        display: block;
        width: max-content;
        margin: 0 auto;

        @include media.mobile {
          width: 100%;
        }

        svg {
          fill: fn.token('surface');
        }
      }
    }
  }

  .about {
    margin: -6.75rem 0 5rem;

    @include media.tablet {
      margin: -9.8rem 0 5rem;
    }

    @include media.mobile {
      margin: -9.4rem 0 2.5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }

      ul.columns {
        padding: 4rem;
        background-color: fn.token('surface');
        box-shadow: shadow.$elevation-3;

        @include flex.start-sb;

        gap: 2rem;

        @include media.tablet {
          gap: 1.5rem;
          padding: 1.5rem;
        }

        @include media.mobile {
          gap: 1.1rem;
          padding: 1.5rem 1rem;
          flex-direction: column;
        }

        li.left {
          max-width: 19.7%;

          @include media.tablet {
            max-width: 16.7804878049%;
          }

          @include media.mobile {
            max-width: 43%;
          }

          img {
            max-width: 100%;
            height: auto;
          }
        }

        li.center {
          max-width: 22%;

          @include media.tablet {
            max-width: 27.5853658537%;
          }

          @include media.mobile {
            max-width: 100%;
            margin-bottom: 0.5rem;
          }
        }

        li.right {
          max-width: 52.4%;

          @include media.tablet {
            max-width: 48.1707317073%;
          }

          @include media.mobile {
            max-width: 100%;
          }
        }

        li {
          width: 100%;

          ul.rows {
            @include flex.stretch-start;

            flex-direction: column;
            gap: 1rem;

            li.row {
              span {
                display: block;

                @include typo.subtitle-3;

                margin-bottom: 0.5rem;
              }

              p {
                @include typo.body-3;
              }
            }
          }
        }
      }
    }
  }

  section.content {
    margin-bottom: 4.5rem;

    @include media.tablet {
      margin-bottom: 4.5rem;
    }

    @include media.mobile {
      margin-bottom: 2.5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(50rem);
      @include flex.start-sb;

      gap: 2rem;

      @include media.mobile {
        padding: 0 1rem;
      }

      :deep(.content) {
        h2 {
          @include typo.subtitle-1;

          margin-bottom: 1rem;
        }

        h3 {
          @include typo.subtitle-2;

          margin-bottom: 1rem;
        }

        p {
          @include typo.body-3;

          margin-bottom: 2rem;

          &:last-of-type {
            margin-bottom: 0;
          }

          a {
            color: fn.token('link');

            @include typo.link-text-3;

            text-decoration: underline;

            &:hover {
              background-color: rgb(fn.token('link'), 16%);
            }
          }
        }

        p + h2 {
          margin-top: 2rem;

          @include media.tablet {
            margin-top: 1.5rem;
          }
        }

        ul,
        ol {
          margin-left: 1rem;
          margin-bottom: 2rem;

          li {
            @include typo.body-3;

            margin-bottom: 0.5rem;

            &:last-of-type {
              margin-bottom: 0;
            }
          }
        }

        ul {
          list-style: disc;
        }

        ol {
          list-style: decimal;
        }
      }
    }
  }

  .feedback {
    margin-bottom: 8.5rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 2.5rem;
    }

    .wrapper {
      padding: 0 2rem;

      @include size.wrapper(80rem);

      @include media.mobile {
        padding: 0 1rem;
      }
    }
  }
}
</style>
